import { HttpError, useList } from "@refinedev/core";

import { Organization } from "@/fhir/types";
import { FIVE_MINUTE_STALE_TIME } from "@/providers/data/fhir-data-provider";
import { ClinicOption } from "@/types/clinic-types";
import { extractClinicInfo, getClinicPrimaryContact } from "@/utils/fhir-clinic-utils";
import { FhirResourceType, formatters } from "@/utils/fhir-utils";

export interface UseClinicsResult {
  clinics: ClinicOption[];
  clinicsRaw: Organization[];
  isLoading: boolean;
  error: HttpError | null;
  refetch: () => void;
}

type UseClinicsOptions = {
  /** When true, follow Bundle next links and return all clinics (uses dataProvider meta.fetchAllPages) */
  fetchAllPages?: boolean;
  /** Optional override for _count when fetchAllPages is enabled */
  pageSize?: number;
  /** When true, includes the parent org in results via _include=Organization:partof */
  includeParentOrg?: boolean;
};

/**
 * Hook to fetch clinics (sub-organizations) for a given parent organization.
 * Default behavior stays the same (single page / refine pagination).
 * Set options.fetchAllPages=true only where you need the full list.
 */
export const useClinics = (parentOrganizationId?: string, options?: UseClinicsOptions): UseClinicsResult => {
  const fetchAllPages = !!options?.fetchAllPages;

  // Defensive: ensure parentOrganizationId is not just truthy but actually a non-empty string
  const hasValidParentOrg = !!parentOrganizationId && parentOrganizationId.trim().length > 0;

  const {
    result,
    query: { isLoading, error, refetch },
  } = useList<Organization>({
    resource: FhirResourceType.Organization,
    filters: hasValidParentOrg
      ? [
          {
            field: "partof",
            operator: "eq",
            value: parentOrganizationId, // keep exactly as before to avoid breaking server behavior
          },
        ]
      : [],
    queryOptions: {
      enabled: hasValidParentOrg,
      staleTime: FIVE_MINUTE_STALE_TIME,
    },
    meta: {
      ...(fetchAllPages && { fetchAllPages: true, pageSize: options?.pageSize ?? 100 }),
      ...(options?.includeParentOrg && {
        customQueryParameters: ["_include=Organization:partof"],
      }),
    },
  });

  const clinicsRaw = (result?.data ?? []) as Organization[];

  const clinics: ClinicOption[] = clinicsRaw.map(clinic => {
    const info = extractClinicInfo(clinic);
    const primaryContact = getClinicPrimaryContact(clinic);

    return {
      id: clinic.id || "",
      value: formatters.formatOrganization(clinic.id || ""),
      label: clinic.name || "",
      phoneNumber: info.phoneNumber,
      active: info.active,
      primaryContactName: primaryContact?.display,
      primaryContactId: primaryContact?.reference?.replace("Practitioner/", ""),
    };
  });

  return {
    clinics,
    clinicsRaw,
    isLoading,
    error: error ?? null,
    refetch,
  };
};
